/**
 * Service pour gérer les appels API liés aux bundles
 * Utilise l'instance axios configurée avec l'authentification automatique
 */

import api from '../utils/api';

// Service pour gérer les bundles côté client
export const bundleService = {
  // Récupère tous les bundles avec filtres
  async getAllBundles(filters = {}) {
    try {
      const params = new URLSearchParams();
      
      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);
      if (filters.bundle_type) params.append('bundle_type', filters.bundle_type);
      if (filters.include_items !== undefined) params.append('include_items', filters.include_items);
      if (filters.include_analytics !== undefined) params.append('include_analytics', filters.include_analytics);
      if (filters.page) params.append('page', filters.page);
      if (filters.pageSize) params.append('pageSize', filters.pageSize);

      const response = await api.get(`/bundles?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des bundles:', error);
      throw error;
    }
  },

  // Récupère un bundle par son ID
  async getBundleById(bundleId) {
    try {
      const response = await api.get(`/bundles/${bundleId}`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération du bundle:', error);
      throw error;
    }
  },

  // Crée un nouveau bundle
  async createBundle(bundleData) {
    try {
      const response = await api.post('/bundles', bundleData);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création du bundle:', error);
      throw error;
    }
  },

  // Met à jour un bundle existant
  async updateBundle(bundleId, bundleData) {
    try {
      const response = await api.put(`/bundles/${bundleId}`, bundleData);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du bundle:', error);
      throw error;
    }
  },

  // Supprime un bundle
  async deleteBundle(bundleId) {
    try {
      const response = await api.delete(`/bundles/${bundleId}`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la suppression du bundle:', error);
      throw error;
    }
  },

  // Calcule le prix d'un bundle
  async calculateBundlePrice(bundleId) {
    try {
      const response = await api.get(`/bundles/${bundleId}/calculate-price`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du calcul du prix du bundle:', error);
      throw error;
    }
  },

  // Récupère les suggestions upsell/downsell
  async getBundleSuggestions(productId, type = 'upsell', limit = 5) {
    try {
      const response = await api.get(`/bundles/suggestions/${productId}?type=${type}&limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des suggestions:', error);
      throw error;
    }
  },

  // Récupère les variantes disponibles pour un bundle
  async getBundleAvailableVariants(bundleId) {
    try {
      const response = await api.get(`/bundles/${bundleId}/variants`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des variantes:', error);
      throw error;
    }
  },

  // Valide un bundle avant ajout au panier
  async validateBundleForCart(bundleId, selectedVariants, quantity = 1) {
    try {
      const response = await api.post(`/bundles/${bundleId}/validate`, {
        selected_variants: selectedVariants,
        quantity
      });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la validation du bundle:', error);
      throw error;
    }
  },

  // Met à jour les analytics d'un bundle
  async updateBundleAnalytics(bundleId, metricType, incrementValue = 1) {
    try {
      const response = await api.post(`/bundles/${bundleId}/analytics`, {
        metric_type: metricType,
        increment_value: incrementValue
      });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour des analytics:', error);
      throw error;
    }
  },

  // Duplique un bundle
  async duplicateBundle(bundleId, nameSuffix = ' (Copie)') {
    try {
      const response = await api.post(`/bundles/${bundleId}/duplicate`, {
        name_suffix: nameSuffix
      });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la duplication du bundle:', error);
      throw error;
    }
  },

  // Récupère les statistiques générales des bundles
  async getBundleStats(dateFrom, dateTo) {
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);

      const response = await api.get(`/bundles/stats/overview?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques:', error);
      throw error;
    }
  },

  // Récupère les analytics détaillées d'un bundle
  async getBundleDetailedAnalytics(bundleId, days = 30) {
    try {
      const response = await api.get(`/bundles/analytics/${bundleId}/detailed?days=${days}`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des analytics détaillées:', error);
      throw error;
    }
  }
};

export default bundleService;